import React from 'react';
import * as timeago from 'timeago.js';
import { Box, Typography } from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material/styles';
import { asArray, displayValue, getEntryTimestamp, limitRows } from '../utils';

const Settings = {
  Name: 'Logs',
  Display: true,
  options: {
    view: null,
    settings: [
      { id: 'accordion', type: 'switch', default: true, name: 'Minimize' },
      { id: 'size', type: 'number', default: 3, name: 'Number of records', min: 1, max: 10 },
      {
        type: 'html',
        html: 'If the number of records is greater than 3, the log is always minimised. If it is 1, the log is always maximised. Logs are capped at 10 items.',
      },
    ],
    grid: true,
  },
};

const Accordion = styled(MuiAccordion)(() => ({
  border: '1px solid rgba(0, 0, 0, .125)',
  boxShadow: 'none',
  '&:not(:last-child)': { borderBottom: 0 },
  '&:before': { display: 'none' },
}));

const AccordionSummary = styled(MuiAccordionSummary)(() => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  borderBottom: '1px solid rgba(0, 0, 0, .125)',
  minHeight: 48,
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
}));

function prettyJson(value) {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch (error) {
    return displayValue(value, 'Unable to display log data');
  }
}

const LogBody = ({ row }) => (
  <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
    {prettyJson(row?.body ?? row)}
  </Box>
);

const LogTitle = ({ row }) => {
  const timestamp = getEntryTimestamp(row);
  const timeAgo = timestamp ? timeago.format(timestamp) : 'Unknown time';
  const absolute = timestamp ? new Date(timestamp).toLocaleString('en-AU') : 'Unknown';

  return <Typography variant="body2">{timeAgo} - {absolute}</Typography>;
};

const View = ({ DeviceData, Settings: userSettings = {} }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [, forceUpdate] = React.useState(0);
  const rows = limitRows(DeviceData, Math.min(Number(userSettings.size) || 3, 10));
  const size = rows.length;

  React.useEffect(() => {
    const firstTimestamp = getEntryTimestamp(asArray(DeviceData)[0]);
    if (!firstTimestamp) return undefined;

    const secondsAgo = Math.max(0, (Date.now() - firstTimestamp) / 1000);
    let delay = 86_400_000;

    if (secondsAgo <= 60) delay = 1000;
    else if (secondsAgo <= 3600) delay = (60 - (secondsAgo % 60)) * 1000;
    else if (secondsAgo <= 86400) delay = (3600 - (secondsAgo % 3600)) * 1000;

    const timer = window.setTimeout(() => forceUpdate((count) => count + 1), delay);
    return () => window.clearTimeout(timer);
  }, [DeviceData]);

  if (!rows.length) return <Box>No logs.</Box>;

  const alwaysOpen = size === 1 || (!userSettings.accordion && size <= 3);

  return (
    <Box>
      {rows.map((row, index) => {
        const key = row?.id || `${getEntryTimestamp(row) || 'row'}-${index}`;

        if (alwaysOpen) {
          return (
            <Box key={key} sx={{ mb: 2 }}>
              <LogTitle row={row} />
              <LogBody row={row} />
            </Box>
          );
        }

        return (
          <Accordion
            key={key}
            square
            expanded={expanded === key}
            onChange={(event, isExpanded) => setExpanded(isExpanded ? key : false)}
          >
            <AccordionSummary>
              <LogTitle row={row} />
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Body</Typography>
              <LogBody row={row} />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default {
  Settings,
  View,
};
